import * as React from "react";
import { Terminal, TriangleAlert, ArrowRight, Activity, ShieldAlert, Clock } from "lucide-react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
const HEX_DATA = ["STRATEGY INNOVATION GROWTH DIGITAL ADVISORY AI SOLUTIONS FUTURE MIND CONSULTING", "0x7F8A9B2C 0x11223344 0xDEADBEEF 0xCAFEBABE 0x8899AABB 0xCCDDEEFF 0x01234567 0x89ABCDEF", "TRANSFORMATION LEADERSHIP ANALYTICS SCALABILITY EFFICIENCY MODERN CONSULTING", "0xFFEEDDCC 0xBBAA9988 0x77665544 0x33221100 0x00112233 0x44556677 0x8899AABB 0xCCDDEEFF", "PRECISION EXECUTION VISIONARY THINKING GLOBAL REACH SUSTAINABLE DEVELOPMENT"];
export default function OurExpertise() {
  return <section data-section-id="234888" id="our-expertise" className="dark bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute inset-0 pointer-events-none">
        {}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--color-border)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-border)_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-[0.05]" />
        
        {}
        <div className="absolute inset-0 flex flex-col justify-around opacity-[0.03] font-mono text-xs md:text-sm whitespace-nowrap overflow-hidden select-none">
          {HEX_DATA.map((line, i) => <div key={i} data-index={i} className="flex gap-4 animate-pulse" style={{
          animationDelay: `${i * 0.5}s`,
          animationDuration: '6s'
        }}>
              <span className="text-primary">{line}</span>
              <span className="text-secondary">{line}</span>
              <span className="text-primary">{line}</span>
              <span className="text-secondary">{line}</span>
            </div>)}
        </div>

        {}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-lg blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[30rem] h-[30rem] bg-secondary/15 rounded-lg blur-[140px]" />
      </div>

      <div className="container max-w-7xl mx-auto px-4 relative z-10">
        {}
        <div className="max-w-3xl mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-secondary/10 border border-primary/20 font-mono text-xs text-primary mb-8 shadow-md">
            <Terminal className="size-4" />
            <span className="tracking-widest uppercase">{`> INITIALIZING STRATEGIC AUDIT... [READY]`}</span>
            <span className="w-1.5 h-3 bg-primary animate-pulse" />
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-default font-bold mb-6 tracking-tighter uppercase">
            Our Core <br className="hidden md:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Expertise</span>
          </h1>
          
          <p className="text-muted-foreground text-lg md:text-xl max-w-2xl font-default leading-relaxed">
            Future Mind Consulting bridges the gap between current operations and future potential through high-end digital transformation and AI-driven strategy.
          </p>
        </div>

        {}
        <div className="relative group">
          {}
          <div className="absolute -inset-0.5 bg-gradient-to-r from-primary/40 to-secondary/40 rounded-2xl blur-xl opacity-20 group-hover:opacity-50 transition duration-700" />
          
          <Card className="relative bg-card/40 backdrop-blur-2xl border-border/40 shadow-4xl overflow-hidden rounded-2xl">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
              
              {}
              <div className="lg:col-span-5 relative min-h-[300px] lg:min-h-full bg-muted/20 border-b lg:border-b-0 lg:border-r border-border/40 overflow-hidden flex items-center justify-center p-8">
                <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688832/img/consulting_innovation_map.png)] bg-cover bg-center opacity-30 mix-blend-overlay" />
                <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-transparent" />
                
                {}
                <div className="relative z-10 w-48 h-48 rounded-lg border border-primary/30 flex items-center justify-center">
                  <div className="absolute inset-0 rounded-lg border border-primary/20 animate-[ping_4s_cubic-bezier(0,0,0.2,1)_infinite]" />
                  <div className="w-32 h-32 rounded-lg border border-secondary/40 flex items-center justify-center">
                    <div className="w-16 h-16 rounded-lg bg-primary/10 flex items-center justify-center backdrop-blur-md">
                      <Activity className="size-8 text-primary" />
                    </div>
                  </div>
                  {}
                  <div className="absolute top-0 bottom-0 left-1/2 w-px bg-primary/20" />
                  <div className="absolute left-0 right-0 top-1/2 h-px bg-primary/20" />
                </div>
              </div>

              {}
              <div className="lg:col-span-7 flex flex-col justify-center p-6 md:p-10">
                <CardHeader className="px-0 pt-0">
                  <div className="flex items-center justify-between mb-4">
                    <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 px-3 py-1 rounded-md">
                      <ShieldAlert className="size-3.5 mr-1.5" />
                      STRATEGIC PILLAR
                    </Badge>
                    <div className="flex items-center text-muted-foreground text-sm font-mono">
                      <Clock className="size-3.5 mr-1.5" />
                      <span>EST_2024_FMC</span>
                    </div>
                  </div>
                  <CardTitle className="text-2xl md:text-3xl font-default font-bold leading-tight mb-4">
                    AI-Driven Business Transformation & Growth
                  </CardTitle>
                  <CardDescription className="text-base md:text-lg text-muted-foreground font-default leading-relaxed">
                    We leverage cutting-edge artificial intelligence to optimize your financial advisory and marketing workflows. Our methodology ensures your business remains resilient and competitive in an increasingly automated global economy.
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="px-0 py-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-background/40 rounded-md p-4 border border-border/40 backdrop-blur-sm">
                      <div className="text-xs text-primary font-mono mb-1 uppercase tracking-widest">Focus Area</div>
                      <div className="font-default font-semibold text-foreground">Digital Strategy</div>
                    </div>
                    <div className="bg-background/40 rounded-md p-4 border border-border/40 backdrop-blur-sm">
                      <div className="text-xs text-secondary font-mono mb-1 uppercase tracking-widest">Impact Level</div>
                      <div className="font-default font-semibold text-primary flex items-center gap-2">
                        High Growth <Activity className="size-4" />
                      </div>
                    </div>
                  </div>
                </CardContent>

                <CardFooter className="px-0 pb-0 pt-2">
                  <Button asChild size="lg" className="w-full sm:w-auto group/btn bg-primary text-primary-foreground hover:bg-primary/90 rounded-md shadow-lg shadow-primary/20">
                    <Link to="#">
                      Explore Our Solutions
                      <ArrowRight className="ml-2 size-4 transition-transform group-hover/btn:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </div>
              
            </div>
          </Card>
        </div>
      </div>
    </section>;
}